import { useState, useEffect } from "react";
import api from "../../../services/api";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface ChartData {
  name: string;
  completed: number;
}

export default function MarketingPerformanceChart() {
  const [data, setData] = useState<ChartData[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        const res = await api.get(`/service-pool?t=${Date.now()}`);
        const pool = Array.isArray(res.data) ? res.data : [];
        
        // Filter for completed expert jobs
        const completedJobs = pool.filter((i: any) => i.type === "EXPERT_JOB" && i.status === "COMPLETED");

        // Group by week (last 8 weeks)
        const groups: Record<string, number> = {};
        const now = new Date();
        
        for (let i = 7; i >= 0; i--) {
          const d = new Date();
          d.setDate(now.getDate() - (i * 7));
          const weekLabel = `WK ${8 - i}`;
          groups[weekLabel] = 0;
        }

        completedJobs.forEach((job: any) => {
          const jobDate = new Date(job.createdAt);
          const diffDays = Math.floor((now.getTime() - jobDate.getTime()) / (1000 * 3600 * 24));
          const weekIndex = 7 - Math.floor(diffDays / 7);
          
          if (weekIndex >= 0 && weekIndex <= 7) {
            const label = `WK ${weekIndex + 1}`;
            groups[label] = (groups[label] || 0) + 1;
          }
        });

        const chartData = Object.entries(groups).map(([name, completed]) => ({
          name,
          completed
        }));

        setData(chartData);
      } catch (err) {
        console.error("Failed to fetch performance data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPerformance();
    const interval = setInterval(fetchPerformance, 30000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="bg-surface-container-low p-8 rounded-[2.5rem] border border-outline-variant/10 shadow-sm h-full flex flex-col items-center justify-center min-h-[350px]">
        <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-[10px] font-black uppercase text-on-surface-variant/40 tracking-widest">Hydrating Analytics...</p>
      </div>
    );
  }
  
  return (
    <div className="bg-surface-container-low p-8 rounded-[2.5rem] border border-outline-variant/10 shadow-sm h-full flex flex-col gap-6 min-h-[350px]">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-black text-on-surface tracking-tighter">Fulfillment Velocity</h3>
          <p className="text-[10px] font-bold text-on-surface-variant/40 uppercase tracking-widest mt-0.5">Quarterly Production Stats</p>
        </div>
        <div className="flex gap-2">
           <div className="flex items-center gap-2 bg-purple-500/10 px-3 py-1 rounded-full">
             <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
             <span className="text-[9px] font-black text-purple-600 uppercase tracking-tighter">Production</span>
           </div>
        </div>
      </div>

      <div className="flex-1 min-h-[300px] w-full mt-4 -ml-6">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#a855f7" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" opacity={0.4} />
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
              dy={10}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 700 }}
              dx={-10}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#ffffff', 
                border: 'none',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: 800,
                color: '#1e293b',
                boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
              }}
              cursor={{ stroke: '#a855f7', strokeWidth: 2, strokeDasharray: '4 4' }}
            />
            <Area 
              type="monotone" 
              dataKey="completed" 
              stroke="#a855f7" 
              strokeWidth={4}
              fillOpacity={1} 
              fill="url(#colorCompleted)" 
              animationDuration={2000}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
